import { GetString } from 'fluent-react';
import React from 'react';
import styled from 'styled-components';
import { semiBoldFontWeight } from '../../cssVariables';
import { CountryProfile, DiversificationGrade, Location } from '../../graphQL/types/shared';
import { gradeToString } from '../../graphQL/Utils';
import { latestYear } from '../../Utils';

const Container = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 6px;
  margin-top: 1rem;
`;

const maxBlocksPerColumn = 10;
const blockSize = 10; // in px
const blockMargin = 2; // in px

const Row = styled.div`
  display: grid;
  grid-template-rows: 1fr 20px;
`;

const ColumnContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const Column = styled.div`
  grid-row: 1;
  display: grid;
  grid-auto-rows: ${blockSize + (blockMargin * 2)}px;
`;

const RowTitle = styled.div`
  border-top: solid 1px white;
  text-align: center;
  grid-row: 2;
`;

const Block = styled.div`
  width: ${blockSize}px;
  height: ${blockSize}px;
  margin: ${blockMargin}px;
  background-color: #d9e8ea;
`;

const CurrentBlock = styled(Block)`
  background-color: #ffa500;
`;

const Title = styled.div`
  font-weight: ${semiBoldFontWeight};
  margin: 0.75rem 0 0.4rem 0;
`;

export interface DiversificationDatum {
  id: Location['id'];
  diversificationGrade: CountryProfile['diversificationGrade'];
  isInCountryPages: Location['isInCountryPages'];
}

interface Grades {
  [DiversificationGrade.APlus]: React.ReactElement[];
  [DiversificationGrade.A]: React.ReactElement[];
  [DiversificationGrade.B]: React.ReactElement[];
  [DiversificationGrade.C]: React.ReactElement[];
  [DiversificationGrade.D]: React.ReactElement[];
  [DiversificationGrade.DMinus]: React.ReactElement[];
}

interface Props {
  diversificationDatum: DiversificationDatum[];
  currentCountryId: Location['id'];
  getFluentString: GetString;
}

const Histogram = (props: Props) => {
  const { diversificationDatum, currentCountryId, getFluentString } = props;
  const gradeColumns: Grades = {
    [DiversificationGrade.APlus]: [],
    [DiversificationGrade.A]: [],
    [DiversificationGrade.B]: [],
    [DiversificationGrade.C]: [],
    [DiversificationGrade.D]: [],
    [DiversificationGrade.DMinus]: [],
  };
  diversificationDatum.forEach(country => {
    const { diversificationGrade, isInCountryPages, id } = country;
    if (isInCountryPages === true && diversificationGrade !== null) {
      const gridColumn =  1 + Math.floor(
        gradeColumns[diversificationGrade].length / maxBlocksPerColumn,
      );
      const gridRow = maxBlocksPerColumn - Math.abs(
        gradeColumns[diversificationGrade].length - ((gridColumn - 1) * maxBlocksPerColumn),
      );
      if (id === currentCountryId) {
        gradeColumns[diversificationGrade].push(<CurrentBlock key={id} style={{ gridColumn, gridRow }} />);
      } else {
        gradeColumns[diversificationGrade].push(<Block key={id} style={{ gridColumn, gridRow }} />);
      }
    }
  });

  const renderRow = (diversificationGrade: DiversificationGrade) => {
    return (
      <Row>
        <RowTitle>{gradeToString(diversificationGrade)}</RowTitle>
        <ColumnContainer>
          <Column>
            {gradeColumns[diversificationGrade]}
          </Column>
        </ColumnContainer>
      </Row>
    );
  };

  return (
    <>
      <Title>{getFluentString('glossary-economic-diversification-grade-histogram-title', {
        year: latestYear.toString(),
      })}</Title>
      <Container>
        {renderRow(DiversificationGrade.APlus)}
        {renderRow(DiversificationGrade.A)}
        {renderRow(DiversificationGrade.B)}
        {renderRow(DiversificationGrade.C)}
        {renderRow(DiversificationGrade.D)}
        {renderRow(DiversificationGrade.DMinus)}
      </Container>
    </>
  );
};

export default Histogram;
